
import { useState } from 'react';

interface HeroProps {
  scrolled: boolean;
}

export default function Hero({ scrolled }: HeroProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <section className="relative min-h-[100svh] flex items-center justify-center overflow-hidden">
      {/* Fade Animation Style */}
      <style>{`
        @keyframes glowPulse {
          0%, 100% { opacity: 0.15; text-shadow: 0 0 8px rgba(212,175,55,0.1); }
          50% { opacity: 1; text-shadow: 0 0 20px rgba(212,175,55,0.4), 0 0 40px rgba(212,175,55,0.15); }
        }
        .glow-pulse {
          animation: glowPulse 3.5s ease-in-out infinite;
        }
      `}</style>

      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/9d55c16919d1fd37fd6eddf2ae1a06cd.jpg"
          alt="New Bedford Harbor"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628]/80 via-[#0A1628]/60 to-[#0A1628]/90"></div>
      </div>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-[#0A1628] shadow-lg shadow-black/20' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 lg:py-6 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <img 
              src="https://public.readdy.ai/ai/img_res/39512e4e-343c-466a-954a-b00d49aed6f0.png" 
              alt="BTC Mobic Logo" 
              className="h-8 sm:h-12 lg:h-14 w-auto"
            />
            <span className="text-lg sm:text-2xl lg:text-3xl font-bold text-white">BTC 모빅</span>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8">
            <a href="#event" className="text-base font-medium text-white hover:text-[#D4AF37] transition-colors whitespace-nowrap cursor-pointer">이벤트</a>
            <a href="#leaderboard" className="text-base font-medium text-white hover:text-[#D4AF37] transition-colors whitespace-nowrap cursor-pointer">순위표</a>
            <a href="#guide" className="text-base font-medium text-white hover:text-[#D4AF37] transition-colors whitespace-nowrap cursor-pointer">참여 안내</a>
            <a href="#event-rewards" className="text-base font-medium text-white hover:text-[#D4AF37] transition-colors whitespace-nowrap cursor-pointer">보상</a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg cursor-pointer text-white"
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden bg-[#0A1628]/95 backdrop-blur-sm border-t border-white/10">
            <div className="px-4 py-3 flex flex-col gap-1">
              <a href="#event" onClick={() => setMenuOpen(false)} className="text-base font-medium py-3 text-white min-h-[44px] flex items-center border-b border-white/5 cursor-pointer">이벤트</a>
              <a href="#leaderboard" onClick={() => setMenuOpen(false)} className="text-base font-medium py-3 text-white min-h-[44px] flex items-center border-b border-white/5 cursor-pointer">순위표</a>
              <a href="#guide" onClick={() => setMenuOpen(false)} className="text-base font-medium py-3 text-white min-h-[44px] flex items-center border-b border-white/5 cursor-pointer">참여 안내</a>
              <a href="#event-rewards" onClick={() => setMenuOpen(false)} className="text-base font-medium py-3 text-white min-h-[44px] flex items-center cursor-pointer">보상</a>
            </div>
          </div>
        )}
      </nav>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-24 sm:pt-32 lg:pt-36 pb-16 sm:pb-24 lg:pb-40">
        <div className="max-w-4xl">
          {/* Label */}
          <div className="inline-flex items-center gap-2 px-3 sm:px-6 py-1.5 sm:py-3 rounded-full border border-white/20 sm:border-2 sm:border-white/30 backdrop-blur-sm mb-3 sm:mb-5 lg:mb-6">
            <span className="text-[#D4AF37] text-[10px] sm:text-sm font-semibold tracking-wider">MAINNET MIGRATION EVENT</span>
          </div>

          {/* Nova Bedfordia Glow Text */}
          <p className="glow-pulse text-[#D4AF37] text-xs sm:text-base lg:text-lg font-medium tracking-[0.15em] sm:tracking-[0.3em] mb-3 sm:mb-5 lg:mb-6 italic">
            Nova Bedfordia &ndash; Lucem Diffundo
          </p>

          {/* Main Title */}
          <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold text-white mb-3 sm:mb-5 leading-tight">
            뉴베드포드:<br />
            <span className="font-light">여정의 시작</span>
          </h1>

          {/* Subtitle */}
          <p className="text-sm sm:text-lg lg:text-xl text-amber-100/90 mb-5 sm:mb-8 leading-relaxed max-w-[90%] sm:max-w-none">
            BTC 모빅 메인넷 마이그레이션을 기념하며,<br className="hidden sm:block" />
            &apos;정박과 항해&apos; 셀프 락업 커밋먼트에 참여하세요.
            <span className="hidden sm:inline"><br />화폐현상을 넘어, 실질적 가치 창출의 시대로</span>
          </p>

          {/* CTA Button */}
          <a 
            href="https://docs.google.com/forms/d/e/1FAIpQLSdIusXkn358TUorU5S7lXeUoqcDzAwZU3mCN5otAbCTx0RKuQ/viewform?usp=header"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2 sm:gap-4 px-5 sm:px-8 py-3.5 sm:py-5 bg-[#D4AF37] hover:bg-[#B8941F] text-[#0A1628] font-bold text-sm sm:text-base rounded-full transition-all duration-300 hover:scale-105 hover:shadow-2xl whitespace-nowrap cursor-pointer min-h-[44px]"
          >
            <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
              <i className="ri-compass-3-line text-lg sm:text-xl"></i>
            </div>
            <span className="uppercase tracking-wider text-xs sm:text-base">셀프 락업 참여하기</span>
            <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
              <i className="ri-arrow-right-up-line text-lg sm:text-xl group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"></i>
            </div>
          </a>

          {/* Event Period - Mobile Optimized */}
          <div className="mt-6 sm:mt-8">
            <div className="inline-block bg-[#0A1628]/60 backdrop-blur-sm border border-white/10 rounded-lg px-4 py-3 sm:px-6 sm:py-4">
              <p className="text-[#708090] text-xs sm:text-base lg:text-lg leading-relaxed">
                이벤트 기간<br />
                <span className="text-white font-semibold text-sm sm:text-lg lg:text-xl">2026.02.19 - 2026.03.18</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-4 sm:bottom-12 left-1/2 -translate-x-1/2 z-10">
        <div className="flex flex-col items-center gap-1.5 animate-bounce">
          <span className="text-white/60 text-[10px] sm:text-xs uppercase tracking-wider">Scroll</span>
          <i className="ri-arrow-down-line text-white/60 text-lg sm:text-xl"></i>
        </div>
      </div>
    </section>
  );
}
